import React from "react";
import { InboxOutlined } from "@ant-design/icons";
import { message, Upload } from "antd";
import * as XLSX from "xlsx";
import swal from "sweetalert";
import { useDispatch } from "react-redux";
import { addNewContact } from "../../store/features/contacListSlice";
import { v4 as uuidv4 } from "uuid";
import PropTypes from "prop-types";

const { Dragger } = Upload;

// Fecha Excel → "YYYY-MM-DD"
function excelDateToJSDate(serial) {
  const utcDays = Math.floor(serial - 25569);
  const utcValue = utcDays * 86400;
  const d = new Date(utcValue * 1000);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Deja solo + y dígitos
const normalizePhoneNumber = (num) =>
  (num ? String(num).replace(/[^\d+]/g, "") : "");

// Sanitizadores
const NAME_LIMIT = 255;
const trimTo = (v, n) => (v ?? "").toString().trim().slice(0, n);

// Validación/sanitización mínima (no falla por longitud: recorta)
function validateAndSanitize(list) {
  const sanitized = [];
  const warnings = [];
  let rowIndex = 1;

  for (const raw of list) {
    const item = { ...raw };

    // persona (nombre) opcional pero lo recortamos si viene
    const originalNombre = (item.nombre ?? "").toString();
    const nombre = trimTo(originalNombre, NAME_LIMIT);
    if (originalNombre.length > NAME_LIMIT) {
      warnings.push(`Row ${rowIndex}: "nombre" truncated to ${NAME_LIMIT} chars.`);
    }

    // empresa (company) opcional pero lo recortamos si viene
    const originalEmpresa = (item.empresa ?? "").toString();
    const empresa = trimTo(originalEmpresa, NAME_LIMIT);
    if (originalEmpresa.length > NAME_LIMIT) {
      warnings.push(`Row ${rowIndex}: "empresa" truncated to ${NAME_LIMIT} chars.`);
    }

    // teléfono obligatorio
    const phone = normalizePhoneNumber(item.numcelular ?? item.telefono);
    if (!phone) {
      return { ok: false, msg: `Row ${rowIndex}: Phone is required.` };
    }

    // email opcional
    const email = (item.email ?? "").toString().trim();

    // fecha opcional
    let date = "";
    const rawDate = item.fecha_nacimiento;
    if (rawDate) {
      date =
        typeof rawDate === "string" && /^\d{4}-\d{2}-\d{2}$/.test(rawDate)
          ? rawDate
          : excelDateToJSDate(rawDate);
    }

    // genero opcional
    const genero = (item.genero ?? "").toString().trim();

    sanitized.push({
      empresa,            // ← company
      nombre,             // ← person name
      email,
      numcelular: phone,
      fecha_nacimiento: date,
      genero,
    });

    rowIndex++;
  }

  return { ok: true, sanitized, warnings };
}

export default function ImportExcelContacts({ setIsModalVisible, navigate }) {
  const dispatch = useDispatch();
  console.log("✅ ImportExcelContacts activo (mapea empresa→name y nombre→lastname)");

  // Carga al store con el mapeo correcto
  const handleUploadContacts = async (rows) => {
    const { ok, sanitized, warnings, msg } = validateAndSanitize(rows);
    if (!ok) {
      await swal("Contact import", msg, "warning");
      setIsModalVisible(false);
      navigate("/contact-list");
      return;
    }

    const ops = sanitized.map((c) =>
      dispatch(
        addNewContact({
          id: uuidv4(),
          // IMPORTANTE:
          // name   = Company/Account Name  (columna "Company" en la tabla)
          // lastname = Name de la persona  (columna "Name" en la tabla)
          name: c.empresa || "",           // ← empresa → name (Company)
          lastname: c.nombre || "",        // ← nombre  → lastname (Name)
          email: c.email || "",
          num: c.numcelular,
          date_birth: c.fecha_nacimiento || "",
          gender: c.genero || "",
        })
      )
    );

    const res = await Promise.allSettled(ops);
    const failed = res.filter((r) => r.status === "rejected");

    if (warnings.length) {
      await swal({
        title: "Imported with warnings",
        text: warnings.join("\n"),
        icon: "info",
      });
    }

    if (!failed.length) {
      await swal("Imported Successfully!", "", "success");
    } else {
      const msgFail = failed.map((f) => JSON.stringify(f.reason)).join("\n\n");
      await swal("Some contacts failed to import", msgFail, "error");
    }

    setIsModalVisible(false);
    navigate("/contact-list");
  };

  // Lectura de archivo y detección de columnas
  const handleFileUpload = async (file) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        let jsonData = [];
        const isCSV = file.type === "text/csv" || file.name.endsWith(".csv");

        if (isCSV) {
          const text = event.target.result;
          const rows = text.split(/\r?\n/).filter(Boolean);
          const headers = rows[0].split(",").map((h) => h.trim());
          jsonData = rows.slice(1).map((row) => {
            const values = row.split(",");
            const obj = {};
            headers.forEach((h, i) => (obj[h] = values[i] ? values[i].trim() : ""));
            return obj;
          });
        } else {
          const data = new Uint8Array(event.target.result);
          const wb = XLSX.read(data, { type: "array" });
          const sheet = wb.SheetNames[0];
          jsonData = XLSX.utils.sheet_to_json(wb.Sheets[sheet]);
        }

        if (!jsonData.length) {
          message.error("File is empty!");
          return;
        }

        // normaliza claves detectadas
        const keysLower = Object.keys(jsonData[0]).map((k) =>
          k.toString().trim().toLowerCase()
        );

        let items;

        // Plantilla: empresa / nombre / telefono
        if (["empresa", "nombre", "telefono"].every((c) => keysLower.includes(c))) {
          items = jsonData.map((row) => {
            const r = {};
            Object.entries(row).forEach(
              ([k, v]) => (r[k.toString().trim().toLowerCase()] = v)
            );
            return {
              empresa: r.empresa ?? "",
              nombre: r.nombre ?? "",
              email: "",
              numcelular: r.telefono ?? "",
              fecha_nacimiento: "",
              genero: "",
            };
          });
        }
        // Estructura exportada completa del sistema
        else if (
          ["id", "name", "lastname", "num", "email", "gender", "date_birth"].every(
            (c) => Object.keys(jsonData[0]).includes(c)
          )
        ) {
          items = jsonData.map((i) => ({
            empresa: i.name ?? "",        // en tu sistema, name = Company
            nombre: i.lastname ?? "",     // lastname = Name de persona
            email: i.email ?? "",
            numcelular: i.num ?? "",
            fecha_nacimiento: i.date_birth ?? "",
            genero: i.gender ?? "",
          }));
        } else {
          message.error("The file does not contain the required columns.");
          return;
        }

        await handleUploadContacts(items);
        message.success(`${file.name} imported successfully!`);
      } catch (e) {
        console.error("Error processing file:", e);
        message.error("Error processing file.");
      }
    };

    if (file.type === "text/csv" || file.name.endsWith(".csv")) {
      reader.readAsText(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  };

  const props = {
    name: "file",
    multiple: true,
    accept: ".xlsx,.csv",
    beforeUpload: (file) => {
      const isXlsx =
        file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const isCsv = file.type === "text/csv" || file.name.endsWith(".csv");
      const isSmall = file.size / 1024 / 1024 < 5;

      if (!isXlsx && !isCsv) return message.error("Only .xlsx or .csv files are allowed."), Upload.LIST_IGNORE;
      if (!isSmall) return message.error("The file cannot be larger than 5MB"), Upload.LIST_IGNORE;
      return true;
    },
    customRequest({ file, onSuccess, onError }) {
      const r = new FileReader();
      r.onload = async () => {
        try {
          await handleFileUpload(file);
          onSuccess("ok");
        } catch (e) {
          onError(e);
          message.error(`${file.name} file upload failed.`);
        }
      };
      r.readAsArrayBuffer(file);
    },
  };

  return (
    <Dragger {...props}>
      <p className="ant-upload-drag-icon">
        <InboxOutlined style={{ color: "green" }} />
      </p>
      <p className="ant-upload-text">Click or drag file to this area to upload</p>
      <p className="ant-upload-hint">Support for a single XLSX/CSV file upload.</p>
    </Dragger>
  );
}

ImportExcelContacts.propTypes = {
  setIsModalVisible: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
};


